import { UserInfo } from './auth-context';

const API_ID_BASE_URL = import.meta.env.VITE_API_ID_BASE_URL;

// Generic API call function with auth
async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {},
  userInfo?: UserInfo | null
): Promise<T> {
  if (!userInfo?.token) {
    throw new Error('Authentication required');
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${userInfo.token}`,
    ...options.headers
  };

  const response = await fetch(`${API_ID_BASE_URL}${endpoint}`, {
    ...options,
    headers
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

// News API functions — public editorial API (WP, edge-cached), same host/base
export async function fetchNews(page: number, perPage: number, country?: string, sort?: string, userInfo?: UserInfo | null) {
  let endpoint = `/editorial/news?perPage=${perPage}&page=${page}`;
  if (country) endpoint += `&country=${country}`;
  if (sort) endpoint += `&sort=${sort}`;
  return apiCall(endpoint, {}, userInfo);
}

export async function fetchNewsById(id: string, userInfo?: UserInfo | null) {
  return apiCall(`/editorial/news/${id}`, {}, userInfo);
}

// Dossiers API functions
export async function fetchDossiers(page: number, perPage: number, country?: string, sort?: string, userInfo?: UserInfo | null) {
  let endpoint = `/editorial/dossiers?perPage=${perPage}&page=${page}`;
  if (country) endpoint += `&country=${country}`;
  if (sort) endpoint += `&sort=${sort}`;
  return apiCall(endpoint, {}, userInfo);
}

export async function fetchDossierById(id: string, userInfo?: UserInfo | null) {
  return apiCall(`/editorial/dossiers/${id}`, {}, userInfo);
}

// Interviews API functions
export async function fetchInterviews(page: number, perPage: number, country?: string, sort?: string, userInfo?: UserInfo | null) {
  let endpoint = `/editorial/interviews?perPage=${perPage}&page=${page}`;
  if (country) endpoint += `&country=${country}`;
  if (sort) endpoint += `&sort=${sort}`;
  return apiCall(endpoint, {}, userInfo);
}

export async function fetchInterviewById(id: string, userInfo?: UserInfo | null) {
  return apiCall(`/editorial/interviews/${id}`, {}, userInfo);
}

// Fetch job categories from the API presets
export async function fetchJobCategories(userInfo?: UserInfo | null) {
  return apiCall(`/jobs/categories`, {}, userInfo);
}

// Fetch a specific job by ID
export async function fetchJobById(id: string, userInfo?: UserInfo | null) {
  return apiCall(`/jobs/${id}`, {}, userInfo);
}

export async function fetchFeaturedJobs(userInfo?: UserInfo | null) {
  return apiCall(`/jobs?premium=1`, {}, userInfo);
}

export async function fetchJobsOffers(url: string, userInfo?: UserInfo | null) {
  return apiCall(url, {}, userInfo);
}

// Email API functions
export async function sendEmail(subject: string, content: string, toEmail: string, userInfo?: UserInfo | null) {
  return apiCall('/email/send', {
    method: 'POST',
    body: JSON.stringify({ subject, content, toEmail })
  }, userInfo);
}

// Opens a pvtistes.net URL via the SSO hand-off so the user lands logged in.
export function openWebRedirect(url: string, userInfo?: UserInfo | null) {
  const idHost = API_ID_BASE_URL.replace(/\/api\/?$/, '');
  let ssoUrl = `${idHost}/sid/sso?return=${encodeURIComponent(url)}`;

  if (userInfo?.token) {
    ssoUrl += `&token=${encodeURIComponent(userInfo.token)}`;
  }

  window.open(ssoUrl, '_blank', 'noopener,noreferrer');
}